import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, FormGroup, Label, Select, Textarea } from '../../styles/PageStyles';
import { useToast } from './ToastProvider';
const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const DrawerContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 800px;
  background: ${({ theme }) => theme?.colors?.background || '#ffffff'};
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s cubic-bezier(0.32, 0.72, 0.3, 1);
  z-index: 1000;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  font-family: 'Manrope', sans-serif;
  color: #1f2937;
  letter-spacing: -0.5px;
`;

const DrawerSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const ConfirmationPrompt = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #92400e;
`;

const BaseButton = styled.button`
  padding: 0.625rem 1.125rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: #ffffff;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: #f3f4f6;
    border-color: #9ca3af;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border-color: #2563eb;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }
`;

const DangerButton = styled(BaseButton)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-color: #ef4444;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-color: #dc2626;
    box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const FieldContainer = styled.div`
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: ${({ $isEditMode }) =>
    $isEditMode ? '#fafbfc' : '#ffffff'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $isEditMode }) =>
      $isEditMode ? '#d1d5db' : '#d1d5db'};
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1f2937;
  font-size: 0.875rem;
  text-transform: capitalize;
  letter-spacing: 0.3px;
`;

const FieldValue = styled.div`
  font-size: 0.875rem;
  color: #374151;
  word-break: break-word;
  line-height: 1.6;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-family: inherit;
  transition: all 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: #f9fafb;
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

// const TextAreaStyled = styled.textarea`
//   width: 100%;
//   padding: 0.625rem 0.75rem;
//   border: 1px solid #d1d5db;
//   border-radius: 0.375rem;
//   font-size: 0.875rem;
//   font-family: 'Courier New', monospace;
//   min-height: 120px;
//   resize: vertical;
//   transition: all 0.2s ease;
//   background: #ffffff;

//   &:focus {
//     outline: none;
//     border-color: #3b82f6;
//     box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
//     background: #f9fafb;
//   }

//   &:hover {
//     border-color: #9ca3af;
//   }
// `;

const MarkdownContainer = styled.div`
  padding: 0.875rem;
  background: #f3f4f6;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;

  .markdown-content {
    font-size: 0.875rem;
    line-height: 1.7;

    h1, h2, h3, h4, h5, h6 {
      margin: 1rem 0 0.5rem 0;
      font-weight: 700;
      color: #1f2937;
    }

    p {
      margin: 0.75rem 0;
    }

    code {
      background: #ffffff;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-family: 'Courier New', monospace;
      color: #dc2626;
    }

    pre {
      background: #1f2937;
      color: #f3f4f6;
      padding: 1rem;
      border-radius: 0.375rem;
      overflow-x: auto;
      margin: 0.75rem 0;

      code {
        background: transparent;
        color: inherit;
        padding: 0;
      }
    }

    ul, ol {
      margin: 0.75rem 0;
      padding-left: 1.75rem;
    }

    li {
      margin: 0.375rem 0;
    }

    blockquote {
      border-left: 4px solid #3b82f6;
      padding-left: 1rem;
      margin: 0.75rem 0;
      color: #6b7280;
      font-style: italic;
    }

    a {
      color: #3b82f6;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }

    table {
      border-collapse: collapse;
      width: 100%;
      margin: 1rem 0;

      th, td {
        border: 1px solid #d1d5db;
        padding: 0.625rem;
        text-align: left;
      }

      th {
        background: #f9fafb;
        font-weight: 700;
      }
    }
  }
`;

const ObjectContainer = styled.div`
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
`;

const ObjectEntry = styled.div`
  margin-bottom: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.5rem;

  strong {
    color: #1f2937;
    font-weight: 700;
  }
`;

const ArrayItem = styled.div`
  margin-bottom: 0.625rem;
  padding: 0.625rem;
  background: #f9fafb;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  font-size: 0.875rem;
`;

const NullValue = styled.span`
  color: #9ca3af;
  font-style: italic;
  font-weight: 500;
`;

const HorizontalRule = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 0;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const ModeToggleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
`;

const ModeToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ModeToggleTitle = styled.div`
  font-weight: 700;
  color: #1f2937;
  font-size: 0.875rem;
`;

const ModeToggleDescription = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-flex;
  width: 54px;
  height: 28px;
  cursor: pointer;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + div {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  }

  &:checked + div::after {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.div`
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #d1d5db;
  transition: 0.3s ease;
  border-radius: 28px;

  &::after {
    position: absolute;
    content: '';
    height: 24px;
    width: 24px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  color: #6b7280;
  font-size: 0.875rem;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;



const EntityFieldRenderer = ({ label, value, isEditMode, onChange }) => {
  const formatString = (str) => {
    return str
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim();
  };

  const isMarkdown = (str) => {
    if (typeof str !== 'string') return false;
    return /^[#-*_\[\]]|```|`/.test(str);
  };

  if (isEditMode && typeof value === 'string') {
    return (
      <FieldContainer $isEditMode={true}>
        <FieldLabel>{formatString(label)}</FieldLabel>
        {value && value.length > 100 ? (
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${formatString(label)}`}
          />
        ) : (
          <TextInput
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${formatString(label)}`}
          />
        )}
      </FieldContainer>
    );
  }

  const renderValue = (val) => {
    if (val === null || val === undefined) {
      return <NullValue>null</NullValue>;
    }

    if (typeof val === 'string') {
      if (isMarkdown(val)) {
        return (
          <MarkdownContainer>
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {val}
              </ReactMarkdown>
            </div>
          </MarkdownContainer>
        );
      }
      return <span>{val}</span>;
    }

    if (typeof val === 'object') {
      if (Array.isArray(val)) {
        return (
          <div>
            {val.length === 0 ? (
              <NullValue>empty array</NullValue>
            ) : (
              val.map((item, idx) => (
                <ArrayItem key={idx}>
                  {typeof item === 'object' ? (
                    Object.entries(item).map(([k, v]) => (
                      <ObjectEntry key={k}>
                        <strong>{formatString(k)}:</strong> {renderValue(v)}
                      </ObjectEntry>
                    ))
                  ) : (
                    renderValue(item)
                  )}
                </ArrayItem>
              ))
            )}
          </div>
        );
      }
      return (
        <ObjectContainer>
          {Object.entries(val).length === 0 ? (
            <NullValue>empty object</NullValue>
          ) : (
            Object.entries(val).map(([k, v]) => (
              <ObjectEntry key={k}>
                <strong>{formatString(k)}:</strong> {renderValue(v)}
              </ObjectEntry>
            ))
          )}
        </ObjectContainer>
      );
    }

    return <span>{String(val)}</span>;
  };

  return (
    <FieldContainer $isEditMode={false}>
      <FieldLabel>{formatString(label)}</FieldLabel>
      <FieldValue>{renderValue(value)}</FieldValue>
    </FieldContainer>
  );
};


const EntityDetailsDrawer = ({
  isDrawerOpen,
  selectedEntity,
  setIsDrawerOpen,
  setSelectedEntity,
  isNewEntity = false,
  setSelectedKbCode,
  entityName = "New",
  excludedFields = [],
  onSave,
}) => {
  const [isEditMode, setIsEditMode] = useState(isNewEntity);
  const [editedData, setEditedData] = useState({});
  const [rawJsonMode, setRawJsonMode] = useState(isNewEntity);
  const [rawJsonData, setRawJsonData] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const { showToast } = useToast();



  useEffect(() => {
    if (selectedEntity) {
      const initialData = Object.entries(selectedEntity)
        .filter(([key]) => !excludedFields.includes(key))
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
      setEditedData(initialData);
      setRawJsonData(JSON.stringify(initialData, null, 2));
      setValidationErrors({});
      setShowDiscardConfirm(false);
    }
  }, [selectedEntity, excludedFields]);

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleDiscardClick = () => {
    if (isEditMode) {
      setShowDiscardConfirm(true);
    } else {
      closeDrawer();
    }
  };

  const handleConfirmDiscard = () => {
    setIsEditMode(false);
    setEditedData(Object.entries(selectedEntity)
      .filter(([key]) => !excludedFields.includes(key))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {}));
    setValidationErrors({});
    setShowDiscardConfirm(false);
    setIsDrawerOpen(false);
  };

  const handleCancelDiscard = () => {
    setShowDiscardConfirm(false);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedEntity(null);
    setIsEditMode(false);
    setRawJsonMode(false);
    setValidationErrors({});
    setShowDiscardConfirm(false);
  };

  const handleFieldChange = (key, value) => {
    setEditedData((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateJSON = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      if (typeof parsed !== 'object' || parsed === null) {
        throw new Error('JSON must be an object');
      }
      return { valid: true, data: parsed };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  };
  console.log("json data:", rawJsonData)

  const handleSaveRawJson = async () => {
    const validation = validateJSON(rawJsonData);
    
    if (!validation.valid) {
      setValidationErrors({
        _general: `Invalid JSON: ${validation.error}`,
      });
      showToast({
        message: `Invalid JSON: ${validation.error}`,
        type: 'error',
      });
      return;
    }

    try {
      setIsSaving(true);
      if (onSave) {
        await onSave(validation.data);
      }
      // showToast({
      //   message: isNewEntity ? 'Entity created successfully!' : 'Entity updated successfully!',
      //   type: 'success',
      // });
      setIsEditMode(false);
      setRawJsonMode(false);
      setValidationErrors({});
      setTimeout(() => closeDrawer(), 500);
    } catch (error) {
      console.error('Error saving entity:', error);
      setValidationErrors({
        _general: error.message || 'Failed to save entity',
      });
      showToast({
        message: error.message || 'Failed to save entity',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveFormData = async () => {
    try {
      setIsSaving(true);
      if (onSave) {
        await onSave(editedData);
      }
      // showToast({
      //   message: isNewEntity ? 'Entity created successfully!' : 'Entity updated successfully!',
      //   type: 'success',
      // });
      setIsEditMode(false);
      setValidationErrors({});
      setTimeout(() => closeDrawer(), 500);
    } catch (error) {
      console.error('Error saving entity:', error);
      setValidationErrors({
        _general: error.message || 'Failed to save entity',
      });
      showToast({
        message: error.message || 'Failed to save entity',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isDrawerOpen || !selectedEntity) return null;

  const fields = Object.entries(selectedEntity).filter(
    ([key]) => !excludedFields.includes(key)
  ) ;

  const renderButtonGroup = () => {
    if (showDiscardConfirm) {
      return (
        <ConfirmationPrompt>
          <span>Discard changes?</span>
          <BaseButton onClick={handleCancelDiscard}>No</BaseButton>
          <DangerButton onClick={handleConfirmDiscard}>Yes</DangerButton>
        </ConfirmationPrompt>
      );
    }

    if (!isEditMode) {
      return (
        <>
          <PrimaryButton onClick={handleEdit}>Edit</PrimaryButton>
          <BaseButton onClick={closeDrawer}>Close</BaseButton>
        </>
      );
    }

    return (
      <>
        <BaseButton 
          onClick={handleDiscardClick} 
          disabled={isSaving}
        >
          Discard
        </BaseButton>
        <PrimaryButton 
          onClick={rawJsonMode ? handleSaveRawJson : handleSaveFormData} 
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </PrimaryButton>
      </>
    );
  };

  return (
    <>
      <DrawerOverlay 
        $isOpen={isDrawerOpen} 
        onClick={!isEditMode ? closeDrawer : undefined}
      />
      <DrawerContainer>
        <DrawerHeader>
          <HeaderContent>
            <DrawerTitle>
              {isNewEntity ? `+ Create ${entityName}` : `Edit ${entityName}`}
            </DrawerTitle>
            {isNewEntity && (
              <DrawerSubtitle>Add new</DrawerSubtitle>
            )}
          </HeaderContent>
          <ButtonGroup>
            {renderButtonGroup()}
          </ButtonGroup>
        </DrawerHeader>

        <HorizontalRule />

        <DrawerContent>
          {validationErrors._general && (
            <ErrorMessage>
              <span>❌</span>
              <span>{validationErrors._general}</span>
            </ErrorMessage>
          )}

          {isEditMode && fields.length > 0 && (
            <ModeToggleSection>
              <ModeToggleLabel>
                <ModeToggleTitle>Raw JSON Mode</ModeToggleTitle>
                <ModeToggleDescription>
                  Edit or Add raw JSON data directly
                </ModeToggleDescription>
              </ModeToggleLabel>
              <Toggle>
                <ToggleInput
                  type="checkbox"
                  checked={rawJsonMode}
                  disabled={isNewEntity || isSaving}
                  onChange={(e) => setRawJsonMode(e.target.checked)}
                />
                <ToggleSlider />
              </Toggle>
            </ModeToggleSection>
          )}

          {/* Loading State */}
          {isSaving && (
            <LoadingState>Saving your changes...</LoadingState>
          )}

          {/* Empty State
          {fields.length === 0 && !isSaving && (
            <EmptyState>No fields to display</EmptyState>
          )} */}

          {/* Raw JSON Mode */}
          {isEditMode && rawJsonMode && fields && !isSaving && (
            <div>
              <FieldLabel style={{ marginBottom: '0.75rem' }}>Entity JSON</FieldLabel>
              <Textarea
                value={rawJsonData}
                onChange={(e) => setRawJsonData(e.target.value)}
                placeholder="Enter valid JSON object"
                style={{ minHeight: '400px' }}
              />
            </div>
          )}

          {/* Form Mode */}
          {isEditMode && !rawJsonMode && fields.length > 0 && !isSaving && (
            fields.map(([key]) => (
              <EntityFieldRenderer
                key={key}
                label={key}
                value={editedData[key]}
                isEditMode={isEditMode}
                onChange={(newValue) => handleFieldChange(key, newValue)}
              />
            ))
          )}

          {/* Read-only Mode */}
          {!isEditMode && fields.length > 0 && !isSaving && (
            fields.map(([key]) => (
              <EntityFieldRenderer
                key={key}
                label={key}
                value={editedData[key]}
                isEditMode={false}
              />
            ))
          )}
        </DrawerContent>
      </DrawerContainer>
    </>
  );
};

export default EntityDetailsDrawer;